import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Wrench, Plus, Heart, History, ShoppingCart, X } from "lucide-react";
import client, { displayName } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { toast } from "sonner";

/** Bottom quick-action bar shown only in Technician Mode. One-handed reorder. */
export const TechnicianBar = () => {
  const { techMode, setTechMode, favourites, mostUsed, addToCart, cartCount } = useStore();
  const [tab, setTab] = useState("used");
  const [items, setItems] = useState([]);
  const navigate = useNavigate();

  const skus = tab === "fav" ? favourites : mostUsed;

  useEffect(() => {
    if (!techMode) return;
    if (!skus.length) { setItems([]); return; }
    client.post("/products/by-skus", { skus: skus.slice(0, 10) }).then((r) => setItems(r.data.items));
  }, [techMode, tab, skus.join(",")]);

  if (!techMode) return null;

  return (
    <div data-testid="technician-bar" className="fixed bottom-0 inset-x-0 z-50 tech-dark border-t border-white/10 shadow-[0_-4px_24px_rgba(0,0,0,0.4)]">
      <div className="max-w-7xl mx-auto px-3 py-2.5">
        <div className="flex items-center gap-2 mb-2">
          <div className="flex items-center gap-1.5 mono text-xs uppercase tracking-[0.2em] text-orange">
            <Wrench className="w-4 h-4" /> Technician Mode
          </div>
          <div className="flex-1" />
          <button data-testid="tech-tab-used" onClick={() => setTab("used")} className={`flex items-center gap-1.5 px-3 h-9 rounded-md text-sm font-medium ${tab === "used" ? "bg-orange text-white" : "bg-white/10 text-white/70"}`}>
            <History className="w-4 h-4" /> Most Used
          </button>
          <button data-testid="tech-tab-fav" onClick={() => setTab("fav")} className={`flex items-center gap-1.5 px-3 h-9 rounded-md text-sm font-medium ${tab === "fav" ? "bg-orange text-white" : "bg-white/10 text-white/70"}`}>
            <Heart className="w-4 h-4" /> Favourites
          </button>
          <button data-testid="tech-cart" onClick={() => navigate("/cart")} className="relative flex items-center gap-1.5 px-3 h-9 rounded-md bg-white text-graphite text-sm font-semibold">
            <ShoppingCart className="w-4 h-4" /> Cart
            {cartCount > 0 && <span className="mono">{cartCount}</span>}
          </button>
          <button data-testid="tech-close" onClick={() => setTechMode(false)} className="w-9 h-9 grid place-items-center rounded-md bg-white/10 text-white/70 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex gap-2 overflow-x-auto thin-scroll pb-1">
          {items.length === 0 && <div className="text-sm text-white/40 py-2">No {tab === "fav" ? "favourite" : "frequently used"} parts yet — add some from the catalogue.</div>}
          {items.map((p) => (
            <button
              key={p.sku}
              data-testid={`tech-add-${p.sku}`}
              onClick={() => { addToCart(p.sku, 1); toast.success(`Added ${p.sku}`); }}
              className="shrink-0 w-44 flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-md p-2 text-left transition-colors"
            >
              <div className="w-11 h-11 rounded bg-white grid place-items-center overflow-hidden shrink-0">
                {p.main_image && <img src={p.main_image} alt="" className="w-full h-full object-contain" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="mono text-xs text-orange truncate">{p.sku}</div>
                <div className="text-xs text-white/80 truncate">{displayName(p)}</div>
              </div>
              <div className="w-8 h-8 grid place-items-center rounded bg-orange text-white shrink-0"><Plus className="w-4 h-4" /></div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
